import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { LogoLockup } from "../components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentReturn({ mode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const started = useRef(false);
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "checking"); // checking | paid | failed | cancelled | timeout
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (mode === "cancel" || started.current) return;
    started.current = true;
    const sid = new URLSearchParams(location.search).get("session_id");
    if (!sid) { setState("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        setDetail(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [location.search, mode]);

  const M = {
    checking: { icon: Loader2, spin: true, color: "#7FA8C9", title: "Confirming your payment…", sub: "This only takes a moment." },
    paid: { icon: CheckCircle2, color: "#22C55E", title: "Payment successful", sub: detail?.invoice_number ? `${detail.invoice_number} is now marked as paid.` : "Thank you — your invoice is settled." },
    failed: { icon: XCircle, color: "#EF4444", title: "Payment not completed", sub: "We couldn't confirm your payment. No charge was made — please try again." },
    cancelled: { icon: XCircle, color: "#F59E0B", title: "Payment cancelled", sub: "You cancelled the payment. Your invoice is still open." },
    timeout: { icon: Loader2, color: "#F59E0B", title: "Still processing", sub: "Your payment is taking a little longer than usual. Check your invoices shortly." },
  }[state];
  const Icon = M.icon;

  return (
    <div data-testid="payment-return" className="min-h-screen bg-ink-950 glow relative flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-md glass rounded-4xl p-10 text-center">
        <div className="mb-8 flex justify-center"><LogoLockup dark className="h-9" /></div>
        <div className="h-16 w-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: `${M.color}22` }}>
          <Icon className={`h-8 w-8 ${M.spin ? "animate-spin" : ""}`} style={{ color: M.color }} data-testid={`payment-icon-${state}`} />
        </div>
        <h1 className="font-serif text-3xl text-white mb-2" style={{ fontWeight: 500 }} data-testid="payment-title">{M.title}</h1>
        <p className="text-white/55 text-sm mb-8">{M.sub}</p>
        {state !== "checking" && (
          <button data-testid="payment-return-portal" onClick={() => navigate("/portal")}
            className="btn-pill bg-white text-ink-950 px-6 py-3 text-sm font-600 inline-flex items-center gap-2">
            Back to Portal <ArrowRight className="h-4 w-4" />
          </button>
        )}
        <div className="mt-6"><Link to="/" className="text-white/40 hover:text-white text-xs">Return to northpeakfs.ca</Link></div>
      </motion.div>
    </div>
  );
}
